import type {
  CampaignCreationCapabilities,
  SmartCreationBrowserDraft,
  SmartCreationStrategyOperation,
  SmartCreationTargetingStrategyKind,
} from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { StrategyStep } from "@/components/smart-creation/StrategyStep";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogHeader, DialogPopup, DialogTitle } from "@/components/ui/dialog";

interface ReviewStrategyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Unsaved target edits or a running command keep the dialog open. */
  locked: boolean;
  plan: SmartCreationBrowserDraft | undefined;
  capabilities: CampaignCreationCapabilities | undefined;
  strategyEditorEpoch: number;
  mutationPending: boolean;
  recommendationPending: boolean;
  onApplyOperations: (
    operations: SmartCreationStrategyOperation[],
    expectedRevision: number,
  ) => Promise<boolean>;
  onAcceptRecommendation: (
    kind: SmartCreationTargetingStrategyKind,
    expectedRevision: number,
  ) => Promise<boolean>;
  onRefreshRecommendation: () => void;
  onLocalDirtyChange: (dirty: boolean) => void;
}

/** Edits strategy targets from the preview step without leaving the Review Workspace. */
export function ReviewStrategyDialog({
  open,
  onOpenChange,
  locked,
  plan,
  capabilities,
  strategyEditorEpoch,
  mutationPending,
  recommendationPending,
  onApplyOperations,
  onAcceptRecommendation,
  onRefreshRecommendation,
  onLocalDirtyChange,
}: ReviewStrategyDialogProps) {
  const { t } = useTranslation("smartCreation");
  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!locked) onOpenChange(nextOpen);
      }}
    >
      <DialogPopup
        className="smart-creation-preview max-w-[1100px]"
        closeProps={{ disabled: locked }}
      >
        <DialogHeader>
          <DialogTitle>{t("strategy.deferred.editTargets")}</DialogTitle>
        </DialogHeader>
        <div className="paper-scrollbar min-h-0 overflow-y-auto px-6 pb-6">
          {plan && capabilities ? (
            <StrategyStep
              key={`${plan.id}:preview:${strategyEditorEpoch}`}
              targetEditing
              plan={plan}
              capabilities={capabilities}
              mutationPending={mutationPending}
              recommendationPending={recommendationPending}
              onApplyOperations={onApplyOperations}
              onAcceptRecommendation={onAcceptRecommendation}
              onRefreshRecommendation={onRefreshRecommendation}
              onLocalDirtyChange={onLocalDirtyChange}
            />
          ) : null}
        </div>
      </DialogPopup>
    </Dialog>
  );
}

/** Confirms a preview regeneration that would drop saved Review edits. */
export function PreviewRegenerationDialog({
  open,
  onSettle,
}: {
  open: boolean;
  onSettle: (confirmed: boolean) => void;
}) {
  const { t } = useTranslation("smartCreation");
  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onSettle(false);
      }}
    >
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("regenerateDialog.title")}</AlertDialogTitle>
          <AlertDialogDescription>{t("regenerateDialog.description")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" />}>
            {t("actions.keepEditing")}
          </AlertDialogClose>
          <Button variant="destructive" onClick={() => onSettle(true)}>
            {t("regenerateDialog.confirm")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}

/** Confirms leaving the workspace with unsaved edits. */
export function DiscardChangesDialog({
  open,
  onOpenChange,
  actionPending,
  onDiscard,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actionPending: boolean;
  onDiscard: () => void;
}) {
  const { t } = useTranslation("smartCreation");
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("closeDialog.discardTitle")}</AlertDialogTitle>
          <AlertDialogDescription>{t("closeDialog.discardDescription")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" />}>
            {t("actions.keepEditing")}
          </AlertDialogClose>
          <Button variant="destructive" disabled={actionPending} onClick={onDiscard}>
            {t("actions.discardClose")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
